import type { Form } from '../../interfaces/form.interface';
import {
    ReactiveFormLoadOrchestrator,
    type ReactiveFormLoadPlan,
    type ReactiveFormLoadPolicy,
    type ReactiveFormLoadReadinessSnapshot,
} from '../load/reactive-form-load-orchestrator';
import { ReactiveFormHostReadinessCoordinator } from './reactive-form-host-readiness-coordinator';
import { ReactiveFormHostState } from './reactive-form-host-state';

export interface ReactiveFormHostLoadCoordinatorPlanOptions<TModel = unknown> {
    readonly state: ReactiveFormHostState<TModel>;
    readonly form: Form<TModel>;
    readonly previousData?: Partial<TModel> | null | undefined;
    readonly policy?: ReactiveFormLoadPolicy | undefined;
    readonly markShellReady?: boolean | undefined;
    readonly markRecordReady?: boolean | undefined;
}

export interface ReactiveFormHostLoadCoordinatorReadinessOptions<TModel = unknown> {
    readonly state: ReactiveFormHostState<TModel>;
    readonly policy?: ReactiveFormLoadPolicy | undefined;
    readonly requireRepeatables?: boolean | undefined;
    readonly requireMinimumReadyTime?: boolean | undefined;
    readonly criticalSupportDataReady?: boolean | undefined;
    readonly deferredCatalogsReady?: boolean | undefined;
    readonly deferredSupportDataReady?: boolean | undefined;
    readonly lazyCatalogsReady?: boolean | undefined;
    readonly hasBlockingErrors?: boolean | undefined;
}

/** Connects the deterministic load plan with host-level readiness flags. */
export class ReactiveFormHostLoadCoordinator {
    public static createPlan<TModel>(options: ReactiveFormHostLoadCoordinatorPlanOptions<TModel>): ReactiveFormLoadPlan {
        const plan = ReactiveFormLoadOrchestrator.createPlan(options.form, {
            previousData: options.previousData ?? options.state.previousData,
            ...(options.policy === undefined ? {} : { policy: options.policy }),
        });

        options.state.setLoadPlan(plan);

        if (options.markShellReady !== false) {
            options.state.markLoadShellReady();
        }

        if (options.markRecordReady === true) {
            options.state.markLoadRecordReady();
        }

        return plan;
    }

    public static snapshot<TModel>(options: ReactiveFormHostLoadCoordinatorReadinessOptions<TModel>): ReactiveFormLoadReadinessSnapshot {
        const readiness = ReactiveFormHostReadinessCoordinator.snapshot({
            state: options.state,
            requireRepeatables: options.requireRepeatables,
            requireMinimumReadyTime: options.requireMinimumReadyTime,
        });

        return ReactiveFormLoadOrchestrator.snapshotReadiness({
            shellReady: options.state.loadShellReady,
            recordReady: options.state.loadRecordReady,
            criticalCatalogsReady: options.state.dropdownsReady,
            hydrationReady: readiness.hydrationReady,
            minTimeExhausted: readiness.minTimeExhausted,
            hasBlockingErrors: options.hasBlockingErrors ?? options.state.loadBlockingErrors,
            ...(options.criticalSupportDataReady === undefined
                ? {}
                : { criticalSupportDataReady: options.criticalSupportDataReady }),
            ...(options.deferredCatalogsReady === undefined
                ? {}
                : { deferredCatalogsReady: options.deferredCatalogsReady }),
            ...(options.deferredSupportDataReady === undefined
                ? {}
                : { deferredSupportDataReady: options.deferredSupportDataReady }),
            ...(options.lazyCatalogsReady === undefined
                ? {}
                : { lazyCatalogsReady: options.lazyCatalogsReady }),
        }, options.policy);
    }
}
